import { Inject, Injectable } from '@angular/core';
import { IonNav, ModalController } from '@ionic/angular';
import { Router } from '@angular/router';

import { AuthProvider } from '../yeti-protocol/auth/provider';
import { UpdateUserProfileData, UserProfile } from '../yeti-protocol/auth/mi';

import { AuthDialogPage, AuthUIServiceInterface } from './auth-ui.service.interface';
import { AuthService } from './auth.service';
import { LinkOpenerService } from '../link-opener.service';
import { LocationService } from '../location.service';

import { AuthSignInPage } from 'src/app/dialogs/auth/auth-signin/auth-signin.page';
import { AuthSignUpContextPage } from 'src/app/dialogs/auth/auth-signup-02-context/auth-signup-context.page';
import { AuthSignUpOtherRolesPage } from 'src/app/dialogs/auth/auth-signup-04-other-roles/auth-signup-other-roles.page';
import { AuthSignUpMainRolesPage } from 'src/app/dialogs/auth/auth-signup-03-main-roles/auth-signup-main-roles.page';
import { AuthSignUpConfirmPage } from 'src/app/dialogs/auth/auth-signup-06-confirm/auth-signup-confirm.page';

import appConfig from 'src/config/config';
import { contextForClient } from 'src/app/contexts/utils';
import { SignInNextStep, SignUpData, SignUpStatus } from './logic/auth-logic.service.interface';
import { AuthWelcomePage } from 'src/app/dialogs/auth/auth-welcome/auth-welcome.page';
import { AuthDialogComponent } from 'src/app/dialogs/auth-dialog/auth-dialog.component';
import { PopupActionType, PopupDialogComponent } from 'src/app/dialogs/popup-dialog/popup-dialog.component';
import { ActionTracked, SignInSignUpTrackingParam, TrackingRequest } from '../yeti-protocol/tracking';
import { DialogForNewlyEnrolledUsersComponent } from
  'src/app/dialogs/dialog-for-newly-enrolled-users/dialog-for-newly-enrolled-users.component';
import { ContextConfigInterface, ContextConfigType, Platform } from 'src/config/config.model';
import { TimeoutService } from '../utils/timeout.service';
import { AppTranslationService } from '../app-translation.service';
import { ContextAddUIService } from '../context/context-add-ui.service';
import { ToastMode, ToastService } from '../toast.service';
import { NavControllerService } from '../nav-controller.service';
import { DeepLinksStorageService } from '../deep-links/deep-links-storage.service';
import { ResponsiveUtilsService } from '../utils/responsive-utils.service';
import { ConfirmDialogData } from 'src/app/services/dialogs/dialogs.ui.interface';
import { DialogsUIService } from '../dialogs/dialogs.ui.service';
import { CONTEXT_SERVICE, ContextService } from '../context/context.model';
import { TrackingService, TRACKING_SERVICE } from '../tracking/tracking.model';
import { ContextSwitchService, CONTEXT_SWITCH_SERVICE } from '../context/context-switch.model';
import { PREVENT_PROFILE_RELOAD_BELLOW_X_MS_DIFF } from './mi/mi-auth.service';
import {AuthOnboardPage} from '../../dialogs/auth/auth-onboard/auth-onboard.page';

type PagesLookup = { [key: string]: any };

const AUTH_PAGE_PATH = '/auth';

interface AuthUIServiceConfig {
  name: string;
  platform: Platform;
  aoServerUrl: string; // TODO: extract in AO specific module
  clientId: string;
  link: { [key: string]: string },
  contexts: Array<ContextConfigType>
}

@Injectable()
export class AuthUIService implements AuthUIServiceInterface {
  nav: IonNav = null;
  config: AuthUIServiceConfig = {
    name: appConfig.name,
    platform: appConfig.platform,
    aoServerUrl: appConfig.authenticationAO?.serverUrl,
    clientId: appConfig.authentication.clientId,
    link: appConfig.link,
    contexts: appConfig.contexts
  };
  pagesLookup: PagesLookup;
  preventDialogOpening = false;
  userProfile: UserProfile = null;
  private static dialogOpeningPromise: Promise<void> = null;

  constructor(
    public authService: AuthService,
    public linkOpener: LinkOpenerService,
    public locationService: LocationService,
    @Inject(CONTEXT_SERVICE) public contextService: ContextService, // TODO: why public ?
    @Inject(CONTEXT_SWITCH_SERVICE) public contextSwitchService: ContextSwitchService,
    public contextAddUIService: ContextAddUIService,
    public router: Router,
    public navController: NavControllerService,
    private modalController: ModalController,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private toast: ToastService,
    private timeoutService: TimeoutService,
    private appTranslationService: AppTranslationService,
    public deepLinkStorage: DeepLinksStorageService,
    private dialogs: DialogsUIService,
    private responsiveUtilsService: ResponsiveUtilsService
  ) {
    this.linkOpener.trackingService = this.trackingService; // TODO: remove dependency
    this.authService.userProfileAsObservable.subscribe(
      userProfile => {
        this.userProfile = userProfile;
      }
    )
    this.pagesLookup = this.buildPagesLookup();
  }

  showAccessBlockedDialog(): Promise<void> {
    return new Promise(resolve => {

      return this.modalController.create({
        component: PopupDialogComponent,
        componentProps: {
          header: this.config.name,
          title: this.appTranslationService.instant('app.services.auth.auth-ui.AccessBlockedDialog.title'),
          message: this.appTranslationService.instant('app.services.auth.auth-ui.AccessBlockedDialog.message'),
          actions: [{
            key: 'showAssistance',
            label: this.appTranslationService.instant('app.services.auth.auth-ui.AccessBlockedDialog.linkLabel'),
            type: PopupActionType.link
          }]
        },
        backdropDismiss: false,
        showBackdrop: true,
      }).then(modalEl => {
        modalEl.onWillDismiss()
          .then(res => {
            if (res.data === 'showAssistance') {
              this.linkOpener.open(this.config.link.accessBlockedHelpUrl);
            }
            if (res.data === 'close') {
              this.router.navigateByUrl(AUTH_PAGE_PATH);
            }
            resolve();
          });
        return modalEl.present();
      });
    });
  }

  async openDialog(startPage: AuthDialogPage): Promise<void> {
    if (this.preventDialogOpening) {
      return Promise.resolve();
    }

    const signUpContexts = await this.getSignUpContexts();
    if (startPage === AuthDialogPage.SignUp02ChooseContext && signUpContexts.length === 1) {
      await this.updateUserProfile({ homeDevision: signUpContexts[0].key });
      startPage = AuthDialogPage.SignUp03ChooseMainRoles;
    }


    const RootPage = this.pagesLookup[startPage];

    let cssClass = 'auth-dialog';

    switch (startPage) {
      case AuthDialogPage.SignUp02ChooseContext:
      case AuthDialogPage.SignUp03ChooseMainRoles:
      case AuthDialogPage.SignUp04ChooseOtherRoles:
      case AuthDialogPage.SignUp06Confirm:
      case AuthDialogPage.Onboard:
        cssClass = `${cssClass} auth-migration-dialog`;

        if (!this.responsiveUtilsService?.isDesktop) {
          cssClass = `${cssClass} mobile`;
        }
        break;
      default:
        break;
    }

    AuthUIService.dialogOpeningPromise = this.modalController.create({
      component: AuthDialogComponent,
      componentProps: {
        rootPage: RootPage,
        rootParams: null
      },
      backdropDismiss: false,
      showBackdrop: true,
      cssClass: cssClass
    }).then(modalEl => {
      return modalEl.present();
    });
    return AuthUIService.dialogOpeningPromise;
  }

  closeDialog(pageUrl?: string): Promise<void> {
    let promise: Promise<void>;
    if (AuthUIService.dialogOpeningPromise) {
      promise = AuthUIService.dialogOpeningPromise.then(() => {
        return this.modalController.dismiss().then(() => {
          return Promise.resolve();
        });
      }).then(() => {
        AuthUIService.dialogOpeningPromise = null;
      });
    } else {
      promise = Promise.resolve();
    }
    return promise.then(() => {
      this.preventDialogOpening = true;
      this.timeoutService.setTimeout(() => {
        this.preventDialogOpening = false;
      }, 1000)
      if (pageUrl) {
        return this.router.navigateByUrl(pageUrl);
      }
    })
      .then(() => {
        return Promise.resolve();
      });
  }

  navigateToHome(contextKey: string = null): Promise<boolean> {
    if (contextKey === null) {
      if (this.isMobile) {
        return this.contextService.getLastSavedContextKey()
          .then(async savedContextKey => {
            if (savedContextKey) {
              return this.contextService.getContext(savedContextKey);
            }

            let context = this.contextService.defaultContext;

            try {
              context = await this.contextService.getCurrentContext();
            } catch (err) {
              console.error(err);
            }

            return context;

          })
          .then(context => {
            return this.navController.navigateRoot(`${context?.homePath}/feed`);
          });
      }
    }
    return this.navController.navigateRoot('/');
  }

  async getSignUpContexts(): Promise<Array<ContextConfigInterface>> {

    let userContext = '';

    try {
      userContext = (await this.contextService.getCurrentContext())?.key;
    } catch (err) {
      userContext = this.config.clientId;
    }

    return this.authService.getSignupStatus(userContext || this.config.clientId).then(status => {
      if (status === SignUpStatus.FINISHED || status === SignUpStatus.UNKNOWN) {
        return this.config.contexts;
      }
      return this.getUserProfile()
        .then(profile => {
          return this.contextSwitchService.getUserContexts(profile)
            .then(res => {
              if (res?.addedContexts?.length > 0) {
                return res.addedContexts;
              }
              return this.config.contexts;
            });
        })
    })
  }


  async finishSignUpIfRequired(): Promise<boolean> {

    let userContext = '';

    try {
      userContext = (await this.contextService.getCurrentContext())?.key;
    } catch (err) {
      userContext = this.config.clientId;
    }

    return this.authService.getSignupStatus(userContext || this.config.clientId)
      .then((status: SignUpStatus) => {
        let Page2open;
        switch (status) {
          case SignUpStatus.NO_HOME_DEVISION:
            Page2open = AuthDialogPage.SignUp02ChooseContext;
            break;
          case SignUpStatus.NO_SPECIALTY:
            Page2open = AuthDialogPage.SignUp03ChooseMainRoles;
            break;
          case SignUpStatus.NOT_CONFIRMED:
            Page2open = AuthDialogPage.SignUp06Confirm;
            break;
          case SignUpStatus.OnBoard:
            Page2open = AuthDialogPage.Onboard;
            break;
        }
        if (!Page2open) {
          return false;
        }
        if (this.nav) {
          return this.navigate(Page2open)
            .then(() => {
              return this.openDialog(Page2open)
                .then(() => { return true; });
            });
        } else {
          return this.openDialog(Page2open)
            .then(() => { return true; });
        }
      });
  }

  get isMobile(): boolean {
    return this.config.platform === Platform.IOS || this.config.platform === Platform.ANDROID;
  }

  buildPagesLookup(): PagesLookup {
    const lookup: PagesLookup = {};
    lookup[AuthDialogPage.SignIn] = AuthSignInPage;
    lookup[AuthDialogPage.Welcome] = AuthWelcomePage;
    lookup[AuthDialogPage.SignUp02ChooseContext] = AuthSignUpContextPage;
    lookup[AuthDialogPage.SignUp03ChooseMainRoles] = AuthSignUpMainRolesPage;
    lookup[AuthDialogPage.SignUp04ChooseOtherRoles] = AuthSignUpOtherRolesPage;
    lookup[AuthDialogPage.SignUp06Confirm] = AuthSignUpConfirmPage;
    lookup[AuthDialogPage.Onboard] = AuthOnboardPage;
    return lookup;
  }

  get availableProviders(): AuthProvider[] {
    const res = [
      AuthProvider.MI
    ];
    if (this.config.aoServerUrl) {
      res.push(AuthProvider.AO);
    }
    return res;
  }

  navigate(page: AuthDialogPage): Promise<any> {
    const Page = this.pagesLookup[page];
    if (this.nav && this.nav.push && Page) {
      return this.nav.push(Page);
    }
    return Promise.reject(`Page is not found: ${page}`);
  }

  signIn(provider: AuthProvider, data: any = {}): Promise<void> {
    return this.authService.signIn(provider, data)
      .then(res => {
        let signInResult;
        if (!res) {
          return this.navigate(AuthDialogPage.Welcome);
        }
        switch (res.nextStep) {
          case SignInNextStep.REDIRECT:
            signInResult = this._redirectAtUrl(res.redirectUrl);
            break;
          case SignInNextStep.WORK:
            signInResult = this._getPathAfterAuthentication()
              .then(async path => {

                await this.modalController.dismiss(null);

                const modal = await this.modalController.getTop();
                if (modal) {
                  this.modalController.dismiss(null);
                }

                // TODO: what to do if context is not in user connected specialties?
                // TODO: According to task MYAO-4658, user should be brought to the deeplink
                // return this.addContextIfItIsMissing(path).then(() => {
                //   return this.navController.navigateRoot(path);
                // });

                return this.navController.navigateRoot(path);
              });
            break;
          case SignInNextStep.MIGRATE:
            signInResult = this.finishSignUpIfRequired();
            break;
          default:
            console.error('not recognised sign in result');
            console.log(res);
        }
        return signInResult;
      });
  }

  _getPathAfterAuthentication(): Promise<string> {
    return this.deepLinkStorage.getSavedDeepLink()
      .then(path => {
        return Promise.all([
          path,
          path ? this.deepLinkStorage.clear() : Promise.resolve(null)
        ]);
      })
      .then(async res => {
        const [path] = res;
        if (path) {

          let deepLinkWithoutQueryParams = path;

          if (deepLinkWithoutQueryParams?.length && deepLinkWithoutQueryParams?.includes('?')) {
            deepLinkWithoutQueryParams = deepLinkWithoutQueryParams.split('?')?.[0];
          }

          if (
            deepLinkWithoutQueryParams &&
            deepLinkWithoutQueryParams !== '/ao-auth-signup' &&
            (deepLinkWithoutQueryParams !== '/auth' || window.location.href.indexOf('/auth') === -1)) {
            // TODO: what to do if context is not in user connected specialties?
            // TODO: According to task MYAO-4658, user should be brought to the deeplink
            return path;
          }
        }

        // single sppecialty app
        if (this.contextService.isSingleContextApp) {
          return this.contextService.defaultContext.homePath;
        }

        // path is not set

        let userContext = '';

        try {
          userContext = (await this.contextService.getCurrentContext())?.key;
        } catch (err) {
          userContext = this.config.clientId;
        }

        return this.authService.getSignupStatus(userContext || this.config.clientId)
          .then(status => {
            if (status === SignUpStatus.FINISHED) {
              return this.authService.getProfile(this.config.clientId, true, PREVENT_PROFILE_RELOAD_BELLOW_X_MS_DIFF)
                .then(profile => {
                  if (profile && profile.homeDevision) {
                    const contextKey = contextForClient(profile.homeDevision);
                    const context = this.contextService.getContext(contextKey);
                    if (context) {
                      return context.homePath;
                    }
                  }
                  console.warn('user profile has no homeDevision');
                  return AUTH_PAGE_PATH; // for migration
                });
            } else {
              return AUTH_PAGE_PATH; // for migration
            }
          });
      });
  }

  signUp(provider: AuthProvider, data: SignUpData): Promise<boolean> {
    return this.authService.signUp(provider, data)
      .then(res => {
        if (res && res.redirectUrl) {
          return this._redirectAtUrl(res.redirectUrl);
        }
        return true;
      });
  }

  _redirectAtUrl(url: string): Promise<any> {
    this.locationService.href = url;
    return new Promise(() => {/* never resolve */ });
  }

  signUpConfirmed(data?: UpdateUserProfileData): Promise<any> {

    let shouldCloseDialog = false;
    data = data || {};
    data.hasAOMigrated = true;
    data.aoMigrationAgreement = true;

    const context = this.userProfile.homeDevision || this.config.clientId;
    return this.authService.updateProfile(context, data)
      .then(async userProfile => {
        this.userProfile = userProfile;
        shouldCloseDialog = true;
        const userRights = await this.authService.getUserRights(null, true);
        if (userRights.hasToOnBoard) {
          shouldCloseDialog = false;
          return this.navigate(AuthDialogPage.Onboard);
        }
        return Promise.resolve();
      })
      .then(() => {
        const selectedHomeContextKey = contextForClient(this.userProfile.homeDevision);
        this.trackSignUpAction(selectedHomeContextKey);
        // TODO: what to do if deep link (deep link case) does not match selected home specialty?
        return this._getPathAfterAuthentication();
      })
      .then(path => {

        if (shouldCloseDialog) {
          shouldCloseDialog = false;
          this.modalController.dismiss(null);
        }

        return this.addContextIfItIsMissing(path).then(() => {

          let pathWithoutQueryParams = path;

          if (pathWithoutQueryParams?.length && pathWithoutQueryParams?.includes('?')) {
            pathWithoutQueryParams = pathWithoutQueryParams.split('?')?.[0];
          }

          if (pathWithoutQueryParams === AUTH_PAGE_PATH) {
            return this.router.navigateByUrl('/');
          }

          return this.navController.navigateRoot(path);
        });
      })
      .then(() => {
        if (!this.userProfile?.aoId) {

          if (shouldCloseDialog) {
            shouldCloseDialog = false;
            this.modalController.dismiss(null);
          }

          return this.confirmEmailAddressReminder()
        }

        return Promise.resolve();
      }).finally(() => {
        if (shouldCloseDialog) {
          this.modalController.dismiss(null);
        }
      });
  }

  addContextIfItIsMissing(pathAfterAuthentication: string): Promise<void> {
    if (!pathAfterAuthentication) {
      return Promise.resolve();
    }
    const parts = pathAfterAuthentication.split('/');
    if (parts.length < 2) {
      return Promise.resolve();
    }
    const contextKey = parts[1];
    const context = this.contextService.getContext(contextKey);
    if (!context) { // path without known context
      return Promise.resolve();
    }
    return this.authService.getSignupStatus(this.config.clientId)
      .then(status => {
        if (status !== SignUpStatus.FINISHED) {
          return true; // ignore untill signup is finished
        }
        return this.contextSwitchService.isContextBelongsToUser(context.key)
      })
      .then(isUserContext => {
        if (isUserContext) {
          return Promise.resolve();
        }
        return this.addMissingContext(context, pathAfterAuthentication);
      });
  }

  addMissingContext(contexToAdd: ContextConfigInterface, deepLink?: string): Promise<void> {
    this.contextAddUIService.addContext(contexToAdd, [contexToAdd], true)
      .then(contextAdded => {
        if (contextAdded) {
          this.contextSwitchService.userContextsUpdated.next();
          if (deepLink) {
            this.router.navigateByUrl(deepLink);
          } else {
            this.locationService.reload();
          }
        } else {
          // navigate to home specialty
          let path = '/';
          if (this.config.platform === Platform.PWA) {
            return this.router.navigateByUrl(path);
          } else {
            // reload home if user cancells adding specialty
            this.getUserProfile()
              .then(profile => {
                const homeContext = this.contextService.getContext(contextForClient(profile.homeDevision));
                if (homeContext) {
                  path = `${homeContext.homePath}/feed`;
                }
              })
              .finally(() => {
                return this.router.navigateByUrl(path);
              });
          }
        }
      })
      .catch(err => {
        this.toast.showWithMessage(err, 'app.common.error-default', ToastMode.ERROR, false, 3000);
      });
    return new Promise(() => {/* never resolve */ });
  }

  updateUserProfile(data: UpdateUserProfileData): Promise<void> {
    const context = data.homeDevision || this.config.clientId;
    return this.authService.updateProfile(context, data)
      .then(userProfile => {
        this.userProfile = userProfile;
      });
  }

  getUserProfile(): Promise<UserProfile> {
    if (this.userProfile) {
      return Promise.resolve(this.userProfile);
    }
    return this.reloadUserProfile();
  }

  reloadUserProfile(): Promise<UserProfile> {
    const context = this?.userProfile?.homeDevision || this.config.clientId;
    return this.authService.reloadProfile(context)
      .then(profile => {
        this.userProfile = profile;
        return profile;
      });
  }

  trackSignUpAction(appId: string, provider?: string): void {
    const paramsToTrack: SignInSignUpTrackingParam = {};
    if (provider) {
      paramsToTrack.provider = provider;
    }

    const trackData: TrackingRequest = {
      action: ActionTracked.signUp,
      appId: appId,
      params: paramsToTrack
    };
    this.trackingService.track(trackData).catch(_err => {
      console.error('Could not track signup confirmed action');
    });
  }

  async showDialogForNewlyEnrolledUsers(): Promise<void> {
    const cssClass = ['dialog-for-newly-enrolled-users'];
    if (this.responsiveUtilsService?.isDesktop) {
      cssClass.push('isDesktop');
    }
    const newlyEnrolledUsersDialog = await this.modalController.create({
      component: DialogForNewlyEnrolledUsersComponent,
      cssClass,
      backdropDismiss: false
    });

    newlyEnrolledUsersDialog.present();

    try {
      await newlyEnrolledUsersDialog.onDidDismiss();
      return Promise.resolve();
    } catch (err) {
      console.error(err);
      return Promise.resolve();
    }
  }

  private async confirmEmailAddressReminder(): Promise<void> {

    const understandActionKey = 'understand';

    const confirmEmailAddressReminderDialogData: ConfirmDialogData = {
      componentProps: {
        title: {
          translationKey: 'app.dialogs.confirm.confirm-email-info-dialog-title'
        },
        message: {
          translationKey: 'app.dialogs.confirm.confirm-email-info-dialog-message'
        },
        actions: [
          {
            key: understandActionKey,
            label: {
              translationKey: 'app.dialogs.confirm.confirm-email-info-dialog-button',
            },
            className: 'primary'
          }
        ]
      }
    }

    const confirmModal = await this.dialogs.createConfirmDialog(confirmEmailAddressReminderDialogData);
    confirmModal.present();

    try {
      await confirmModal?.onDidDismiss();
      return Promise.resolve();
    } catch (err) {
      console.error(err);
      return Promise.resolve();
    }
  }
}
